import { useState } from 'react';
import {
  ArrowRight,
  Plus,
  Trash2,
  Wallet,
  Banknote,
  Smartphone,
  AlertCircle,
  Users,
} from 'lucide-react';

import { Modal } from '@/components/Modal';
import { useConfirm } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import { formatNPR, parsePriceInput } from '@/components/Money';
import { FormulaHint } from '@/components/FormulaHint';
import { InfoHint } from '@/components/InfoHint';
import { RefreshButton } from '@/components/RefreshButton';
import { PageShell } from '@/components/PageShell';
import { usePermissions } from '@/lib/permissions';
import {
  useAccountBalances,
  useTransfers,
  useCreateTransfer,
  useDeleteTransfer,
  useCafeBalance,
  type AccountBalance,
} from '@/lib/api';

// The three accounts the operator sees and moves money between. Historical
// rows may still carry eSewa / Khalti / card / other values; those roll up
// under "Online" in the backend (see methodsForBalances in accounts.go) so
// the FE only needs labels for the three canonical buckets.
const METHOD_LABEL: Record<string, string> = {
  cash: 'Cash drawer',
  online: 'Online',
  bank: 'Bank',
};

const METHOD_ICON: Record<string, typeof Banknote> = {
  cash: Banknote,
  online: Smartphone,
  bank: Wallet,
};

function methodLabel(m: string): string {
  return METHOD_LABEL[m] ?? m;
}

export function AccountsPage() {
  const { can } = usePermissions();
  // Balances are owner-only: the API has always gated /finance/cafe-balance on
  // finance:read, which no non-owner role holds by default. The nav entry and
  // this page were gated on account:read instead, so a manager reached it and
  // saw real per-account figures beside a total that 403'd to zero. The
  // transfer surface is a separate question — a manager still records a
  // cash-to-bank deposit without being shown what the cafe is worth.
  const canSeeBalances = can('finance:read');
  const balance = useCafeBalance(canSeeBalances);
  const balances = useAccountBalances(canSeeBalances);
  const transfers = useTransfers();
  const [transferring, setTransferring] = useState(false);
  const [transferDefaults, setTransferDefaults] = useState<{ from?: string; to?: string }>({});

  return (
    <PageShell
      eyebrow={canSeeBalances ? 'Money on hand' : 'Moving money between accounts'}
      title={canSeeBalances ? 'Cafe balance' : 'Transfers'}
      actions={
        <>
          <RefreshButton
            onClick={() =>
              Promise.all(
                canSeeBalances
                  ? [balance.refetch(), balances.refetch(), transfers.refetch()]
                  : [transfers.refetch()],
              )
            }
            busy={
              (canSeeBalances && (balance.isFetching || balances.isFetching)) ||
              transfers.isFetching
            }
            label="Refresh"
          />
          {can('transfer:create') && (
            <button
              type="button"
              className="btn primary"
              onClick={() => {
                setTransferDefaults({});
                setTransferring(true);
              }}
            >
              <ArrowRight size={14} strokeWidth={1.5} /> Move money
            </button>
          )}
        </>
      }
    >
      {/* HERO — total balance + drawer / bank / online breakdown. Owner-only:
       * this is every rupee the cafe holds. */}
      {canSeeBalances && (
      <section
        style={{
          padding: 22,
          background: 'linear-gradient(135deg, var(--ink-900) 0%, var(--ink-800) 100%)',
          border: '1px solid var(--ink-700)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-4)',
        }}
      >
        {balance.isError && !balance.data && <ErrorState compact onRetry={() => balance.refetch()} />}
        {!(balance.isError && !balance.data) && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 'var(--space-6)',
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-2xs)',
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: 'var(--ink-300)',
                }}
              >
                total cafe balance
                <FormulaHint
                  topic="cafe-balance"
                  label="Total cafe balance"
                  cents={balance.data?.total_cents ?? 0}
                  terms={[
                    { label: 'Cash drawer', cents: balance.data?.drawer_cents ?? 0 },
                    {
                      label: 'Online',
                      cents: (balance.data?.channels ?? []).reduce(
                        (sum, c) => sum + c.balance_cents,
                        0,
                      ),
                    },
                    { label: 'Bank', cents: balance.data?.bank_cents ?? 0 },
                    { label: 'With owners', cents: balance.data?.owner_cash_cents ?? 0 },
                  ]}
                  note={
                    <>
                      Every rupee the cafe holds, wherever it currently sits. Money guests
                      still owe on credit is deliberately NOT here — it isn’t the cafe’s
                      until it’s collected.
                    </>
                  }
                />
              </div>
              <div
                className="num"
                style={{
                  fontFamily: 'var(--font-num)',
                  fontSize: 44,
                  color: 'var(--lime-fg)',
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1.1,
                  marginTop: 2,
                }}
              >
                {balance.isPending ? '…' : formatNPR(balance.data?.total_cents ?? 0)}
              </div>
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-xs)',
                  letterSpacing: '0.06em',
                  color: 'var(--ink-400)',
                  marginTop: 'var(--space-2)',
                }}
              >
                {balance.data?.drawer_source === 'live'
                  ? 'live · drawer reflects open shift'
                  : balance.data?.drawer_source === 'last_close'
                  ? `drawer as of last close · ${
                      balance.data.drawer_as_of
                        ? new Date(balance.data.drawer_as_of).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : ''
                    }`
                  : 'no shift activity yet'}
              </div>
            </div>

            {/* Breakdown chips — three peer accounts, no accent. The hero
             * num above already carries the visual weight; tiles only need
             * to label each pool of money. Order matches the flow: cash
             * collected → online collected → bank (where both end up after
             * a transfer). */}
            <div
              style={{
                display: 'flex',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              {/* This is the SHIFT drawer figure (live while a shift is open,
                  otherwise the last counted close). The "Cash drawer" card lower
                  down is the lifetime cash ledger — a different question, so the
                  two legitimately differ. Both say which they are. */}
              <BreakdownTile
                icon={<Banknote size={14} strokeWidth={1.5} />}
                label="Drawer · this shift"
                cents={balance.data?.drawer_cents ?? 0}
                hint={
                  <InfoHint topic="drawer-vs-ledger">
                    What the till should hold right now: the open shift’s float plus its
                    cash in, minus cash out. When no shift is open it’s the last counted
                    closing figure. The <strong>Cash drawer</strong> card below answers a
                    different question — every cash rupee the cafe has ever taken, less
                    everything cash has paid for — so the two are not the same number.
                  </InfoHint>
                }
              />
              <BreakdownTile
                icon={<Smartphone size={14} strokeWidth={1.5} />}
                label="Online"
                cents={(balance.data?.channels ?? []).reduce((s, c) => s + c.balance_cents, 0)}
              />
              <BreakdownTile
                icon={<Wallet size={14} strokeWidth={1.5} />}
                label="Bank"
                cents={balance.data?.bank_cents ?? 0}
              />
              {(balance.data?.owner_cash_cents ?? 0) !== 0 && (
                <BreakdownTile
                  icon={<Users size={14} strokeWidth={1.5} />}
                  label="With owners"
                  cents={balance.data?.owner_cash_cents ?? 0}
                />
              )}
            </div>
          </div>
        )}

        {/* Outstanding loans pill */}
        {(balance.data?.owner_outstanding.loans_cents ?? 0) > 0 && (
          <div
            style={{
              marginTop: 'var(--space-4)',
              padding: '10px 14px',
              background: 'rgba(var(--amber-glow), 0.08)',
              border: '1px solid rgba(var(--amber-glow), 0.25)',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 'var(--text-md)',
              color: 'var(--amber-fg)',
            }}
          >
            <AlertCircle size={14} strokeWidth={1.5} />
            <span>
              <strong>{formatNPR(balance.data?.owner_outstanding.loans_cents ?? 0)}</strong>{' '}
              owed back to owners (pocket-paid expenses awaiting reimbursement)
            </span>
          </div>
        )}
      </section>
      )}

      {/* Per-account tiles. Three known buckets — cash drawer, online, bank
       * — so the grid has a stable shape. Reserving the min-height stops
       * the panel from shrinking to nothing during the brief moment between
       * the initial render and the first refetch. */}
      {canSeeBalances && (
      <section className="panel">
        <div className="panel-head">
          <h3>Accounts</h3>
          <span className="meta">Live · refreshes every 30s</span>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 10,
            minHeight: 168,
          }}
        >
          {balances.isPending && (
            <div style={{ gridColumn: '1 / -1' }}>
              <LoadingState compact />
            </div>
          )}
          {balances.isError && !balances.data && (
            <div style={{ gridColumn: '1 / -1' }}>
              <ErrorState compact onRetry={() => balances.refetch()} />
            </div>
          )}
          {balances.data?.map((a) => (
            <BalanceCard
              key={a.method}
              acct={a}
              canTransfer={can('transfer:create')}
              onTransferFrom={() => {
                setTransferDefaults({ from: a.method });
                setTransferring(true);
              }}
              onTransferTo={() => {
                setTransferDefaults({ to: a.method });
                setTransferring(true);
              }}
            />
          ))}
        </div>
      </section>
      )}

      <section className="panel" style={{ marginTop: canSeeBalances ? 'var(--space-4)' : 0 }}>
        <div className="panel-head">
          <h3>Transfers</h3>
          <span className="meta">Last 200</span>
        </div>

        {transfers.isPending && <LoadingState />}
        {transfers.isError && !transfers.data && <ErrorState onRetry={() => transfers.refetch()} />}
        {transfers.data?.length === 0 && (
          <div className="empty-state">
            No transfers yet — moving cash from drawer to bank, or eSewa to bank, lands here.
          </div>
        )}
        {transfers.data && transfers.data.length > 0 && (
          <table className="t">
            <thead>
              <tr>
                <th>When</th>
                <th>From → To</th>
                <th>Reference</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th style={{ textAlign: 'right' }}>Fee</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {transfers.data.map((t) => (
                <TransferRow key={t.id} t={t} />
              ))}
            </tbody>
          </table>
        )}
      </section>

      <TransferModal
        open={transferring}
        defaults={transferDefaults}
        onClose={() => setTransferring(false)}
      />
    </PageShell>
  );
}

// -------------------------------------------------------------------------

function BreakdownTile({
  icon,
  label,
  cents,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  cents: number;
  hint?: React.ReactNode;
}) {
  return (
    <div
      style={{
        padding: 'var(--space-3) var(--space-4)',
        background: 'var(--ink-950)',
        border: '1px solid var(--ink-800)',
        borderRadius: 'var(--radius-md)',
        minWidth: 140,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--ink-300)',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--text-2xs)',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
        }}
      >
        {icon}
        {label}
        {hint}
      </div>
      <div
        className="num"
        style={{
          fontFamily: 'var(--font-num)',
          fontSize: 20,
          color: cents >= 0 ? 'var(--ink-50)' : 'var(--danger-fg)',
          fontVariantNumeric: 'tabular-nums',
          marginTop: 'var(--space-1)',
        }}
      >
        {formatNPR(cents)}
      </div>
    </div>
  );
}

function BalanceCard({
  acct,
  canTransfer,
  onTransferFrom,
  onTransferTo,
}: {
  acct: AccountBalance;
  canTransfer: boolean;
  onTransferFrom: () => void;
  onTransferTo: () => void;
}) {
  const positive = acct.balance_cents >= 0;
  const Icon = METHOD_ICON[acct.method] ?? Wallet;
  // Credit collected is money in against sales billed on earlier days. It used
  // to be folded into payments_cents and rendered as "sales", which made a tab
  // being paid off look like the sale had been counted twice.
  const creditIn = acct.credit_collected_cents ?? 0;
  // The signed remainder that isn't a sale, a collection, an expense or a
  // transfer: for cash, owner draws and recount corrections; for bank, owner
  // capital and owner-held cash that has been banked. Shown so the card's parts
  // visibly add up to its balance.
  const movements = acct.other_movements_cents ?? 0;
  return (
    <div
      style={{
        padding: 14,
        background: 'var(--ink-900)',
        border: '1px solid var(--ink-800)',
        borderRadius: 'var(--radius-sm)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--ink-300)',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--text-2xs)',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
        }}
      >
        <Icon size={12} strokeWidth={1.5} />
        {acct.label}
        <FormulaHint
          topic="account-balance"
          label={`${acct.label} balance`}
          cents={acct.balance_cents}
          terms={[
            { label: 'Sales collected here', cents: acct.payments_cents },
            { label: 'Credit collected', cents: creditIn, note: '(earlier sales)' },
            { label: 'Expenses paid from here', cents: acct.expenses_cents, op: '−' },
            { label: 'Transfers in', cents: acct.transfers_in_cents },
            { label: 'Transfers out', cents: acct.transfers_out_cents, op: '−',
              note: '(includes any fee)' },
            movements >= 0
              ? { label: 'Other movements', cents: movements }
              : { label: 'Other movements', cents: -movements, op: '−' as const },
          ]}
          note={
            acct.method === 'cash' ? (
              <>
                Every cash rupee the cafe has ever taken, less everything cash has paid
                for — a lifetime ledger. The <strong>Drawer · this shift</strong> figure
                above answers “what should be in the till right now”, so the two differ.
              </>
            ) : (
              <>
                Everything this account has received, less what has been paid out of it.
                Money guests owe on credit isn’t here until it’s collected.
              </>
            )
          }
        />
      </div>
      <div
        className="num"
        style={{
          fontFamily: 'var(--font-num)',
          fontSize: 22,
          color: positive ? 'var(--ink-50)' : 'var(--danger-fg)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatNPR(acct.balance_cents)}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--space-1)',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--text-2xs)',
          color: 'var(--ink-400)',
          paddingTop: 6,
          borderTop: '1px solid var(--ink-800)',
        }}
      >
        <span>+ {formatNPR(acct.payments_cents)} sales</span>
        <span style={{ textAlign: 'right' }}>+ {formatNPR(acct.transfers_in_cents)} in</span>
        {creditIn > 0 && (
          <span style={{ gridColumn: '1 / -1', color: 'var(--ink-300)' }}>
            + {formatNPR(creditIn)} credit collected · pays off earlier sales, not new sales
          </span>
        )}
        <span>− {formatNPR(acct.expenses_cents)} expenses</span>
        <span style={{ textAlign: 'right' }}>− {formatNPR(acct.transfers_out_cents)} out</span>
        {movements !== 0 && (
          <span style={{ gridColumn: '1 / -1', color: 'var(--ink-300)' }}>
            {movements > 0 ? '+' : '−'} {formatNPR(Math.abs(movements))}{' '}
            {acct.method === 'cash'
              ? 'owner draws & recount corrections'
              : 'owner capital & banked owner cash'}
          </span>
        )}
      </div>
      {canTransfer && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <button
            type="button"
            className="btn small"
            style={{ flex: 1 }}
            onClick={onTransferFrom}
            title={`Move money out of ${acct.label}`}
          >
            <ArrowRight size={10} strokeWidth={1.5} /> Out
          </button>
          <button
            type="button"
            className="btn small"
            style={{ flex: 1 }}
            onClick={onTransferTo}
            title={`Move money into ${acct.label}`}
          >
            <ArrowRight size={10} strokeWidth={1.5} style={{ transform: 'rotate(180deg)' }} /> In
          </button>
        </div>
      )}
    </div>
  );
}

function TransferRow({
  t,
}: {
  t: {
    id: string;
    from_method: string;
    to_method: string;
    amount_cents: number;
    fee_cents: number;
    reference_no: string;
    transferred_at: string;
    shift_id?: string | null;
  };
}) {
  const { can } = usePermissions();
  const del = useDeleteTransfer();
  const confirm = useConfirm();
  const fromLabel = methodLabel(t.from_method);
  const toLabel = methodLabel(t.to_method);
  return (
    <tr>
      <td className="sku">
        {new Date(t.transferred_at).toLocaleString('en-GB', {
          day: '2-digit',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </td>
      <td>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <span className="pill">{fromLabel}</span>
          <ArrowRight size={12} strokeWidth={1.5} style={{ color: 'var(--ink-400)' }} />
          <span className="pill ok">{toLabel}</span>
        </span>
      </td>
      <td className="sku">{t.reference_no || '—'}</td>
      <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)' }}>
        {formatNPR(t.amount_cents)}
      </td>
      <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)' }}>
        {t.fee_cents > 0 ? formatNPR(t.fee_cents) : '—'}
      </td>
      <td>
        {can('transfer:delete') && (
          <button
            type="button"
            className="btn icon danger"
            onClick={async () => {
              const ok = await confirm({
                title: 'Delete transfer?',
                message: (
                  <>
                    Reverse the <strong>{formatNPR(t.amount_cents)}</strong> transfer from{' '}
                    <strong>{fromLabel}</strong> to <strong>{toLabel}</strong>? Both account
                    balances will be restored.
                  </>
                ),
                danger: true,
              });
              if (ok) del.mutate(t.id);
            }}
            aria-label="delete"
            disabled={del.isPending}
          >
            <Trash2 size={12} strokeWidth={1.5} />
          </button>
        )}
      </td>
    </tr>
  );
}

// -------------------------------------------------------------------------

// Only the three operator-visible accounts can be a transfer endpoint.
// The backend enforces the same allow-list (see CreateTransfer in
// accounts.go); historical eSewa / Khalti / card / other rows still
// exist on past transfers but never as a destination going forward.
const TRANSFERABLE = ['cash', 'online', 'bank'];

function TransferModal({
  open,
  defaults,
  onClose,
}: {
  open: boolean;
  defaults: { from?: string; to?: string };
  onClose: () => void;
}) {
  const create = useCreateTransfer();
  const [fromMethod, setFromMethod] = useState(defaults.from ?? 'online');
  const [toMethod, setToMethod] = useState(defaults.to ?? 'bank');
  const [amount, setAmount] = useState('');
  const [fee, setFee] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);

  // Refresh defaults whenever the modal re-opens.
  if (open && defaults.from && fromMethod !== defaults.from && amount === '') {
    setFromMethod(defaults.from);
  }
  if (open && defaults.to && toMethod !== defaults.to && amount === '') {
    setToMethod(defaults.to);
  }

  return (
    <Modal open={open} onClose={onClose} title="Move Money" subtitle="Transfer between accounts">
      {err && <div className="banner-error">{err}</div>}
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setErr(null);
          const cents = parsePriceInput(amount);
          if (cents == null || cents <= 0) {
            setErr('amount required');
            return;
          }
          if (fromMethod === toMethod) {
            setErr('from and to must differ');
            return;
          }
          try {
            await create.mutateAsync({
              from_method: fromMethod,
              to_method: toMethod,
              amount_cents: cents,
              fee_cents: parsePriceInput(fee) ?? 0,
              reference_no: reference,
              notes,
            });
            setAmount('');
            setFee('');
            setReference('');
            setNotes('');
            onClose();
          } catch (e: unknown) {
            setErr((e as { message?: string }).message ?? 'Failed');
          }
        }}
      >
        <div className="row-inputs">
          <div className="field">
            <label>From</label>
            <select value={fromMethod} onChange={(e) => setFromMethod(e.target.value)}>
              {TRANSFERABLE.map((m) => (
                <option key={m} value={m}>
                  {METHOD_LABEL[m] ?? m}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>To</label>
            <select value={toMethod} onChange={(e) => setToMethod(e.target.value)}>
              {TRANSFERABLE.map((m) => (
                <option key={m} value={m}>
                  {METHOD_LABEL[m] ?? m}
                </option>
              ))}
            </select>
          </div>
        </div>

        {(fromMethod === 'cash' || toMethod === 'cash') && (
          <div className="banner-info" style={{ marginBottom: 14 }}>
            cash side requires an open shift — the matching drawer movement is recorded
            automatically.
          </div>
        )}

        <div className="row-inputs">
          <div className="field">
            <label>Amount (NPR)</label>
            <input
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="5000"
              autoFocus
              required
            />
          </div>
          <div className="field">
            <label>Fee (NPR, optional)</label>
            <input
              inputMode="decimal"
              value={fee}
              onChange={(e) => setFee(e.target.value)}
              placeholder="0"
            />
          </div>
        </div>

        <label>Reference</label>
        <input
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="bank slip no, transaction id…"
        />

        <label>Notes</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary" disabled={create.isPending}>
            <Plus size={12} strokeWidth={1.5} />
            {create.isPending ? 'Saving…' : 'Record transfer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
